/* ----------------- React --------------- */
import { useEffect, useState, useTransition } from "react";

/* ----------------- Constants --------------- */
import { getAuthToken } from "@helpers/auth-provider";
import { authHeaders } from "@memorang/ui/contexts/AuthTokenContext";

/* ----------------- Features --------------- */
import { SESSION_EVENTS } from "../constants";
import useSessionStore from "./useSessionStore";

import i18n from "@helpers/lib/i18n";
/* ----------------- Helpers --------------- */
import type { BaseItemSchema } from "@memorang/types";

import { HUB_BASE_URL } from "@constants/env";
import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { useAppStore } from "@hooks/useAppStore";
import { HUB_ENDPOINTS } from "@memorang/constants/urls";
import { getPlatform } from "@memorang/helpers/platform";
import type { Hint } from "@memorang/types";
/* ----------------- React Query --------------- */
import { useMutation } from "@tanstack/react-query";
import { randomUUID } from "expo-crypto";
import { fetch as expoFetch } from "expo/fetch";
import { buildItemCacheIdentity } from "../helpers/item-cache-identity";
import {
	buildOriginalQuestionData,
	getSelectionTexts,
	resolveChoiceExplanation,
} from "../helpers/original-question";
import { getParentStem } from "./useAIFeedback";
import useAIFeedbackStore from "./useAIFeedbackStore";

export const useHint = ({
	item,
	type = "item",
	selectedChoice,
	selectedChoiceId,
}: {
	item: BaseItemSchema;
	type?: "item" | "choice";
	selectedChoice?: string;
	selectedChoiceId?: string;
}) => {
	const { id: itemId, metadata, tags } = item;

	const topics = metadata?.topics || tags?.map((tag) => tag.label);

	const appId = useAppStore((store) => store.app.id);

	const parentStem = getParentStem(item);

	const {
		currentExamContext: { examId, organizationId, projectId },
	} = useExamContext();

	const [hints, setHints] = useState<Hint[]>([]);
	const { userLogicalId } = useAuthContext();
	const addSessionEvent = useSessionStore((state) => state.addSessionEvent);

	const platform = getPlatform();
	const [loading, setLoading] = useState(false);
	const [streaming, setStreaming] = useState(false);

	const [pending, startTransition] = useTransition();
	const updateHintsForItem = useSessionStore(
		(state) => state.updateHintsForItem,
	);

	const { mutate: getHint } = useMutation({
		mutationKey: selectedChoiceId
			? ["hint", itemId, selectedChoiceId]
			: ["hint", itemId],
		mutationFn: async () => {
			const sessionState = useSessionStore.getState();
			const resolved = buildOriginalQuestionData({
				item,
				answer: sessionState.answers?.get(itemId!) ?? item.answer,
				itemState: sessionState.itemStates?.get(itemId!),
				formStates: sessionState.formStates?.[itemId!],
				liveResponse: useAIFeedbackStore.getState().userResponse?.[itemId!],
			});
			const cacheIdentity = buildItemCacheIdentity({
				organizationId,
				projectId,
				appId,
				examId,
				itemId,
				itemVersion: item.versionId,
				locale: i18n.language,
			});
			const params = {
				platform,
				itemId: typeof itemId === "string" ? itemId : itemId?.toString(),
				examId: `${examId}`,
				originalQuestion: {
					topics,
					explanation: resolveChoiceExplanation(item),
					stem: resolved.stem,
					incorrectChoices: resolved.incorrectChoices,
					correctChoice: resolved.correctChoice,
					selectedChoice:
						selectedChoice ||
						getSelectionTexts(resolved.selectedChoice).join("; "),
				},
				type,
				userId: userLogicalId,
				appId,
				outputLanguage: i18n.language,
				extraDetails: parentStem,
				// Omitted (not null) when incomplete: the server's legacy hint key
				// is stable per item, so falling back keeps responses cached.
				...(cacheIdentity ? { cacheIdentity } : {}),
				...(sessionState.id ? { sessionId: sessionState.id } : {}),
			};
			setLoading(true);
			const token = await getAuthToken();
			const response = await expoFetch(`${HUB_BASE_URL}${HUB_ENDPOINTS.HINT}`, {
				method: "POST",
				headers: {
					Accept: "text/event-stream",
					...authHeaders(token),
				},
				body: JSON.stringify(params),
			});
			const reader = response.body?.getReader();
			return reader;
		},
		onSuccess: (reader) => {
			const hintId = randomUUID();
			const hintIndex = hints.length;

			addSessionEvent(itemId!, {
				name: SESSION_EVENTS.HINT_REQUESTED,
				props: {
					hintId,
					hintIndex,
				},
			});

			setLoading(false);
			setStreaming(true);
			setHints((prevHints) => [
				...prevHints,
				{
					id: hintId,
					text: "",
				},
			]);
			const decoder = new TextDecoder("utf-8");
			let result = "";

			async function read() {
				if (reader) {
					const { done, value } = await reader.read();
					if (done) {
						setStreaming(false);
						return;
					}
					result += decoder.decode(value);
					startTransition(() => {
						setHints((prevHints) => {
							const lastIdx = prevHints.length - 1;
							if (lastIdx < 0) {
								return prevHints;
							}
							const next = [...prevHints];
							const last = next[lastIdx];
							if (!last) {
								return prevHints;
							}
							next[lastIdx] = { ...last, text: result };
							return next;
						});
					});
					await read();
				}
			}
			read();
		},
	});

	useEffect(() => {
		if (hints.length > 0 && !loading && !streaming && !pending) {
			updateHintsForItem(itemId!, hints);
		}
	}, [hints, loading, streaming, itemId, pending, updateHintsForItem]);

	return {
		hints,
		loading,
		streaming,
		getHint,
	};
};
